import { useMemo, useState } from 'react';
import {
  engagementTypeLabel,
  type CalendarEventSummary,
} from '@todo/shared';

/**
 * Time-split analytics: where the visible window's booked time actually
 * goes, as a donut with the total in the middle.
 *
 * WHY A DONUT AND NOT A PIE: the hole holds the number people want first -
 * "7h 29m booked" - so the chart answers the headline question without a
 * second glance. The slices then answer "on what".
 *
 * WHY AT MOST THREE SLICES: the palette's hues only clear the
 * colour-blindness and normal-vision separation floors for three when any
 * two slices may be compared. Anything past three folds into "Other"
 * rather than inventing a fourth hue nobody can name. Each slice is also
 * direct-labelled in the legend with its hours, so identity never rests on
 * colour alone.
 *
 * It reads the FILTERED events, so it always describes exactly what the
 * calendar beside it is showing.
 *
 * WHY THE RED OUTER RING IS NOT A SLICE: overlap is not a fourth category -
 * the same minutes are already inside one of the slices. It rides outside
 * the donut as a separate red ring, and it is always spelled out in words
 * next to the counts, so it is never colour alone.
 */

/** Slice colours. Stepped for the dark card surface and validated as a set:
 *  lightness band, chroma, CVD separation, contrast - all pass. */
const SLICE_COLORS = ['#8b5cf6', '#16a34a', '#0284c7'] as const;
const MEETING_GREEN = '#16a34a';
const ENGAGEMENT_VIOLET = '#8b5cf6';

export interface Slice {
  key: string;
  label: string;
  minutes: number;
  color: string;
}

function durationLabel(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function minutesOf(ev: CalendarEventSummary): number {
  return Math.max(
    0,
    (new Date(ev.endsAt).getTime() - new Date(ev.startsAt).getTime()) / 60_000,
  );
}

/**
 * What counts as booked time.
 *
 * A CANCELLED entry is not a commitment, and a meeting the caller DECLINED
 * is not their time - counting either would inflate the total with work
 * that is not happening. Overlaps are counted twice on purpose: this is
 * time BOOKED, not time occupied, and two meetings in one slot really were
 * two bookings.
 */
function counts(ev: CalendarEventSummary): boolean {
  if (ev.status === 'CANCELLED') return false;
  if (ev.myResponse === 'DECLINED') return false;
  return true;
}

function buildSlices(
  events: CalendarEventSummary[],
  tab: 'ALL' | 'MEETING' | 'ENGAGEMENT',
): Slice[] {
  const live = events.filter(counts);

  if (tab === 'MEETING') {
    const mine = live.filter((e) => e.kind === 'MEETING' && e.isOwner);
    const invited = live.filter((e) => e.kind === 'MEETING' && !e.isOwner);
    return [
      { key: 'mine', label: 'Organised by me', minutes: sum(mine), color: SLICE_COLORS[1] },
      { key: 'invited', label: 'Invited', minutes: sum(invited), color: SLICE_COLORS[2] },
    ].filter((s) => s.minutes > 0);
  }

  if (tab === 'ENGAGEMENT') {
    /* Keyed by the type CODE - open now that types are org-owned rows. */
    const byType = new Map<string, number>();
    for (const e of live) {
      if (e.kind !== 'ENGAGEMENT') continue;
      /* The type CODE; engagementTypeLabel prettifies a custom one. */
      const t: string = e.engagementType ?? 'OTHER';
      byType.set(t, (byType.get(t) ?? 0) + minutesOf(e));
    }
    const ranked = [...byType].sort((a, b) => b[1] - a[1]);
    // Two named types plus everything else - the three-slice ceiling.
    const named = ranked.slice(0, 2);
    const rest = ranked.slice(2).reduce((acc, [, m]) => acc + m, 0);
    const slices: Slice[] = named.map(([type, minutes], i) => ({
      key: type,
      label: engagementTypeLabel(type),
      minutes,
      color: SLICE_COLORS[i]!,
    }));
    if (rest > 0) {
      slices.push({ key: 'other', label: 'Others', minutes: rest, color: SLICE_COLORS[2] });
    }
    return slices;
  }

  const meetings = live.filter((e) => e.kind === 'MEETING');
  const engagements = live.filter((e) => e.kind === 'ENGAGEMENT');
  return [
    { key: 'meetings', label: 'Meetings', minutes: sum(meetings), color: MEETING_GREEN },
    { key: 'engagements', label: 'Public Meet', minutes: sum(engagements), color: ENGAGEMENT_VIOLET },
  ].filter((s) => s.minutes > 0);
}

function sum(events: CalendarEventSummary[]): number {
  return events.reduce((acc, e) => acc + minutesOf(e), 0);
}

/**
 * Booked time that collides with something else. `hasConflict` is decided
 * server-side over the whole calendar, not just the visible window, so an
 * entry can be flagged here even when the thing it clashes with is filtered
 * out of view - which is exactly what you want from a warning.
 */
function clashing(events: CalendarEventSummary[]): CalendarEventSummary[] {
  return events.filter((e) => counts(e) && e.hasConflict);
}

export interface ClashGroup {
  key: string;
  /** Two or more entries sharing one stretch of the clock. */
  events: CalendarEventSummary[];
}

/**
 * Who actually collides with whom.
 *
 * A sweep line over the visible entries: sorted by start, an entry joins the
 * open group while it begins before the group's furthest end, otherwise it
 * opens a new one. Grouping is therefore transitive - A/B and B/C land in
 * one group of three - which is what you want on screen, because that is
 * one contested stretch of the day, not two separate problems.
 *
 * `hasConflict` is decided server-side over the WHOLE calendar, so an entry
 * can be flagged while its partner is filtered out of view. Those come back
 * as `elsewhere` and are listed apart rather than silently dropped.
 */
function clashGroups(events: CalendarEventSummary[]): {
  groups: ClashGroup[];
  elsewhere: CalendarEventSummary[];
} {
  const live = [...events.filter(counts)].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
  );

  const groups: ClashGroup[] = [];
  const grouped = new Set<string>();
  let open: CalendarEventSummary[] = [];
  let openEnd = -Infinity;

  const close = () => {
    if (open.length > 1) {
      groups.push({ key: open[0]!.id, events: open });
      for (const e of open) grouped.add(e.id);
    }
    open = [];
    openEnd = -Infinity;
  };

  for (const ev of live) {
    const start = new Date(ev.startsAt).getTime();
    const end = new Date(ev.endsAt).getTime();
    // Touching edges (one ends exactly as the next starts) is not a clash.
    if (start < openEnd) {
      open.push(ev);
      openEnd = Math.max(openEnd, end);
    } else {
      close();
      open = [ev];
      openEnd = end;
    }
  }
  close();

  const elsewhere = live.filter((e) => e.hasConflict && !grouped.has(e.id));
  return { groups, elsewhere };
}

function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

/** One donut arc as a stroked circle, positioned by dash offset. */
function Arc({
  slice, total, radius, circumference, offset,
}: {
  slice: Slice;
  total: number;
  radius: number;
  circumference: number;
  offset: number;
}) {
  const fraction = slice.minutes / total;
  // A 2px gap between neighbouring fills, so adjacent slices never read as
  // one shape. Never longer than the arc itself.
  const gap = Math.min(2, fraction * circumference * 0.5);
  const length = Math.max(0, fraction * circumference - gap);
  return (
    <circle
      cx="50" cy="50" r={radius}
      fill="none"
      stroke={slice.color}
      strokeWidth="14"
      strokeDasharray={`${length} ${circumference - length}`}
      strokeDashoffset={-offset}
      transform="rotate(-90 50 50)"
    >
      <title>
        {slice.label}: {durationLabel(slice.minutes)} ({Math.round(fraction * 100)}%)
      </title>
    </circle>
  );
}

export function TimeSplitCard({
  events, tab, rangeLabel, onSelectEvent,
}: {
  /** The FILTERED events - what the calendar is showing right now. */
  events: CalendarEventSummary[];
  tab: 'ALL' | 'MEETING' | 'ENGAGEMENT';
  /** "This week" / "This month". */
  rangeLabel: string;
  /** Opens one entry from the overlap list. The element is the popover anchor. */
  onSelectEvent?: (event: CalendarEventSummary, anchor: HTMLElement) => void;
}) {
  const slices = useMemo(() => buildSlices(events, tab), [events, tab]);
  const total = slices.reduce((acc, s) => acc + s.minutes, 0);
  const counted = useMemo(() => events.filter(counts), [events]);
  const clashes = useMemo(() => clashing(events), [events]);
  const clashMinutes = sum(clashes);
  const { groups, elsewhere } = useMemo(() => clashGroups(events), [events]);
  const [open, setOpen] = useState(false);
  const showList = open && clashes.length > 0;

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  // The overlap ring sits outside the slices on its own track.
  const clashRadius = 47;
  const clashCircumference = 2 * Math.PI * clashRadius;
  const clashArc = total > 0
    ? Math.min(1, clashMinutes / total) * clashCircumference
    : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      {/*
        The exclusion note rides beside the heading rather than under the
        chart: it qualifies every number in the card, and putting it up here
        keeps the card short enough that the sidebar does not scroll.
      */}
      <div className="flex items-start justify-between gap-2">
        <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {rangeLabel}
        </p>
        <p className="min-w-0 flex-1 text-left text-[10px] leading-tight text-slate-400">
          Cancelled entries and meetings you declined are left out.
        </p>
      </div>

      {total === 0 ? (
        <p className="mt-2 text-xs text-slate-400">
          Nothing booked in this view.
        </p>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-3">
            <svg viewBox="0 0 100 100" className="h-20 w-20 shrink-0" role="img"
              aria-label={`Booked time split: ${slices
                .map((s) => `${s.label} ${durationLabel(s.minutes)}`)
                .join(', ')}${clashes.length > 0
                  ? `. ${durationLabel(clashMinutes)} of it overlaps another entry.`
                  : ''}`}>
              {/* The track keeps the ring readable when one slice is tiny. */}
              <circle cx="50" cy="50" r={radius} fill="none"
                className="stroke-slate-100" strokeWidth="14" />
              {slices.map((slice, i) => (
                <Arc
                  key={slice.key}
                  slice={slice}
                  total={total}
                  radius={radius}
                  circumference={circumference}
                  offset={slices
                    .slice(0, i)
                    .reduce((acc, s) => acc + (s.minutes / total) * circumference, 0)}
                />
              ))}
              {clashArc > 0 && (
                <circle
                  cx="50" cy="50" r={clashRadius}
                  fill="none"
                  className="stroke-red-700"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${clashArc} ${clashCircumference - clashArc}`}
                  transform="rotate(-90 50 50)"
                >
                  <title>
                    {durationLabel(clashMinutes)} overlaps another meeting or event
                  </title>
                </circle>
              )}
              {/* The headline lives in the hole - the reason this is a donut. */}
              <text x="50" y="47" textAnchor="middle"
                className="fill-slate-900 text-[15px] font-semibold">
                {durationLabel(total)}
              </text>
              <text x="50" y="60" textAnchor="middle"
                className="fill-slate-400 text-[8px] uppercase tracking-wide">
                booked
              </text>
            </svg>

            <ul className="min-w-0 flex-1 space-y-1">
              {slices.map((s) => (
                <li key={s.key} className="flex items-center gap-2 text-xs">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: s.color }} />
                  <span className="min-w-0 flex-1 truncate text-slate-600">{s.label}</span>
                  <span className="shrink-0 font-semibold text-slate-800">
                    {durationLabel(s.minutes)}
                  </span>
                  <span className="w-8 shrink-0 text-right text-slate-400">
                    {Math.round((s.minutes / total) * 100)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/*
        Counts last, on one line, so the chart keeps the top of the card.
        The overlap figure is red AND labelled - a red ring on its own would
        be unreadable to anyone who cannot separate the hues.
      */}
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2 text-xs">
        <span className="text-slate-500">
          Events <span className="font-semibold text-slate-800">{counted.length}</span>
        </span>
        {clashes.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={showList}
            className="flex items-center gap-1.5 rounded-md px-1 py-0.5 font-semibold text-red-700 underline-offset-2 transition hover:bg-red-50 hover:underline"
            title={`${clashes.length} entr${clashes.length === 1 ? 'y' : 'ies'} overlap another meeting or event (${durationLabel(clashMinutes)}). Click to see which.`}
          >
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-red-700" />
            {clashes.length} overlapping
            <svg viewBox="0 0 12 12" aria-hidden="true"
              className={`h-3 w-3 transition-transform ${showList ? 'rotate-180' : ''}`}>
              <path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor"
                strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <span className="text-slate-400">No overlaps</span>
        )}
      </div>

      {/*
        Which entries collide, grouped by the stretch of clock they fight
        over - a bare count tells you there is a problem but not where it
        is. Capped in height so opening it never pushes the sidebar into a
        scroll; the list scrolls instead.
      */}
      {showList && (
        <div className="mt-2 max-h-48 space-y-2 overflow-y-auto border-t border-red-200 pt-2">
          {groups.map((g) => (
            <div key={g.key}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700">
                {dayLabel(g.events[0]!.startsAt)} · {clockLabel(g.events[0]!.startsAt)}
                {' - '}
                {clockLabel(
                  g.events.reduce((late, e) =>
                    new Date(e.endsAt) > new Date(late.endsAt) ? e : late,
                  ).endsAt,
                )}
              </p>
              <ul className="mt-0.5">
                {g.events.map((ev) => (
                  <li key={ev.id}>
                    <button
                      type="button"
                      onClick={(e) => onSelectEvent?.(ev, e.currentTarget)}
                      className="flex w-full items-baseline gap-2 rounded-md px-1 py-1 text-left transition hover:bg-slate-50"
                    >
                      <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                        {ev.title}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-slate-400">
                        {clockLabel(ev.startsAt)}-{clockLabel(ev.endsAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {elsewhere.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Clashes with something outside this view
              </p>
              <ul className="mt-0.5">
                {elsewhere.map((ev) => (
                  <li key={ev.id}>
                    <button
                      type="button"
                      onClick={(e) => onSelectEvent?.(ev, e.currentTarget)}
                      className="flex w-full items-baseline gap-2 rounded-md px-1 py-1 text-left transition hover:bg-slate-50"
                    >
                      <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                        {ev.title}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-slate-400">
                        {dayLabel(ev.startsAt)} {clockLabel(ev.startsAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
